import React from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionItemValue,
  SectionTitle,
} from '@/components/ui'
import { type CompradorResumo } from '@/features/proposta/brasil/domain/ResumoProposta/resumo-compradores-proposta'

type TCompradoresSectionProps = {
  onEdit: (step: number) => void
  resumo: {
    readonly titular: CompradorResumo
    readonly compradores: readonly CompradorResumo[]
  }
}

const STEP_CADASTRO_COMPRADORES = 1

export function CompradoresSection({ resumo, onEdit }: Readonly<TCompradoresSectionProps>) {
  const { t } = useTranslation('proposta')

  return (
    <div>
      <SectionHeader>
        <SectionTitle>{t('resumo_proposta.compradores.titulo')}</SectionTitle>
        <div className="hidden sm:block">
          <Button
            variant="ghost"
            className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900"
            onClick={() => onEdit(STEP_CADASTRO_COMPRADORES)}
          >
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionHeader>
      <SectionContent>
        <SectionGroup withBorder>
          <SectionItem label={t('resumo_proposta.compradores.label.titular')}>
            <SectionItemValue>{resumo.titular.nome}</SectionItemValue>
          </SectionItem>
        </SectionGroup>
        <SectionGroup withBorder={resumo.compradores.length > 0} className="border-neutral-300">
          <SectionItem label={resumo.titular.tipoDocumento} colSpan={6} withRightBorder>
            <SectionItemValue>{resumo.titular.documento}</SectionItemValue>
          </SectionItem>
          <SectionItem label={t('resumo_proposta.compradores.label.participacao')} colSpan={6}>
            <SectionItemValue>{resumo.titular.participacao}</SectionItemValue>
          </SectionItem>
        </SectionGroup>

        {resumo.compradores.map((comprador, index) => (
          <React.Fragment key={comprador.documento}>
            <SectionGroup withBorder>
              <SectionItem label={t('resumo_proposta.compradores.label.comprador')}>
                <SectionItemValue>{comprador.nome}</SectionItemValue>
              </SectionItem>
            </SectionGroup>
            <SectionGroup withBorder={index < resumo.compradores.length - 1} className="border-neutral-300">
              <SectionItem label={comprador.tipoDocumento} colSpan={6} withRightBorder>
                <SectionItemValue>{comprador.documento}</SectionItemValue>
              </SectionItem>
              <SectionItem label={t('resumo_proposta.compradores.label.participacao')} colSpan={6}>
                <SectionItemValue>{comprador.participacao}</SectionItemValue>
              </SectionItem>
            </SectionGroup>
          </React.Fragment>
        ))}

        <div className="block sm:hidden">
          <Button variant="outline" className="w-full" onClick={() => onEdit(STEP_CADASTRO_COMPRADORES)}>
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionContent>
    </div>
  )
}
